import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import LeadsTable from "@/pages/leads/components/LeadsTable/LeadsTable";
import LeadsBottomKPIBar from "@/components/controls/BottomKPIBar/LeadsBottomKPIBar";
import Spinner from "@/components/ui/spinner";
import {
  useLeadsFilterOptions,
  useLeadsGroupByOptions
} from "@/pages/leads/filters";
import { useLeadsInfinite, useResetLeadsInfinite } from "@/pages/leads/hooks";
import { LeadColumn, LeadData } from "@/pages/leads/types";
import { leadGroupAggregationField } from "@/pages/leads/groupSummaries";
import { leadsColumnKeys } from "@/pages/leads/helpers";
import { ComputedFields, Filters } from "@/services/api/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId
} from "@/entities/dataTables/helpers";
import { useEntityFields, useFieldVisibility } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { APIFieldItem } from "@/pages/settings/types";
import { getPartnerBaseFilters } from "@/pages/partners/helpers";
import { useUsersMe } from "@/entities/users/hooks";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import PartnerTabEmptyState from "./PartnerTabEmptyState";

// Distinct from the standalone "leads-table" so the scoped tab keeps its own
// column/view state, while getPageNameFromTableId still resolves to "leads".
const TABLE_ID = "leads-partner-table";

const leadsSortByOptions = [
  LeadColumn.NAME,
  LeadColumn.COMPANY_NAME,
  LeadColumn.STATUS,
  LeadColumn.OPEN_FOR_DAYS
];

interface ContentProps {
  customFields: APIFieldItem[];
  baseFilters: Filters;
  partnerId: string;
}

// Rendered only once the partner field has resolved, so `filters` can seed
// directly to the partner scope — the first fetch is already filtered and the
// table never flashes the full, unscoped list.
const PartnerLeadsTabContent: React.FC<ContentProps> = ({
  customFields,
  baseFilters,
  partnerId
}) => {
  const { t } = useTranslation();
  const { data: userData } = useUsersMe();
  const resetLeads = useResetLeadsInfinite();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Filters>(baseFilters);
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: LeadColumn.NAME, desc: false }
  ]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.LEADS_TABLE)
  );

  const { filterVisibleColumnKeys } = useFieldVisibility(
    EntityType.LEAD
  );
  const activeLeadsColumnKeys = useMemo(
    () => filterVisibleColumnKeys(leadsColumnKeys),
    [filterVisibleColumnKeys]
  );
  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activeLeadsColumnKeys,
        customFields,
        getPageNameFromTableId(TABLE_ID),
        t
      ),
    [activeLeadsColumnKeys, customFields, t]
  );
  // Opening a view saved without columns empties the layout; re-seed it then.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    setColumnsState((prev) => (prev.length === 0 ? defaultColumnsState : prev));
  }, [defaultColumnsState, isColumnsStateEmpty]);

  const handleColumnsChange = useCallback((updated?: ColumnData[]) => {
    if (updated) setColumnsState(updated);
  }, []);

  const sortParams = useMemo(() => {
    const fieldId = sorting[0]?.id ?? LeadColumn.NAME;
    const field = fieldId === LeadColumn.CONTACT ? "contactFirstName" : fieldId;
    return { field, asc: sorting[0] ? !sorting[0].desc : true };
  }, [sorting]);

  // Same query either way, so grouping it by the field the table groups on
  // costs nothing and gives every group its figures — including the groups
  // infinite scroll has not reached.
  const aggregationsGroupField = (leadGroupAggregationField(groupBy[0]) ??
    "all") as keyof LeadData;

  const {
    entities: leads,
    hasNextPage,
    isLoading,
    isAggregationsLoading,
    isAggregationsError,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    aggregations,
    total
  } = useLeadsInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    aggregationsGroupField,
    undefined,
    sortParams.field,
    sortParams.asc,
    false,
    true,
    ComputedFields.MINIMAL
  );

  const leadsFilterOptions = useLeadsFilterOptions(leads);
  const leadsGroupByOptions = useLeadsGroupByOptions();

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  };

  // Nothing linked yet — search, filters and column display have nothing to act
  // on, so the empty state stands alone. Once filters or a search term are what
  // emptied the table, the toolbar stays so the user can undo them.
  const isPristine = isEmpty(searchTerm) && isEqual(filters, baseFilters);
  const showEmptyState =
    isPristine && !isLoading && !isError && isEmpty(leads);

  return (
    <div className="flex flex-col h-full">
      {showEmptyState ? null : (
        <LeadsBottomKPIBar
          aggregations={aggregations}
          totalLeads={total}
          isLoading={isLoading || isAggregationsLoading}
          isError={isAggregationsError}
          isRowsError={isError}
        />
      )}
      <ToolbarActions
        className={showEmptyState ? "hidden" : "p-2 pr-4"}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={leadsFilterOptions}
        baseFilters={baseFilters}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={leadsGroupByOptions}
        sorting={sorting}
        onSortingChange={onSortingChange}
        sortByOptions={leadsSortByOptions}
        defaultSortByValue={LeadColumn.NAME}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={ViewType.LEADS_TABLE}
        onColumnsChange={handleColumnsChange}
        tableId={TABLE_ID}
        customFields={customFields}
      />
      <LeadsTable
        data={leads}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onLoadMore={handleLoadMore}
        filters={filters}
        setFilters={setFilters}
        filterOptions={leadsFilterOptions}
        sorting={sorting}
        onSortingChange={onSortingChange}
        groupBy={groupBy}
        aggregations={aggregations}
        isAggregationsLoading={isAggregationsLoading}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        customFields={customFields}
        selectedView={selectedView}
        tableId={TABLE_ID}
        hasActiveFilters={!isEqual(filters, baseFilters)}
        onClearFilters={() => setFilters(baseFilters)}
        onClearSearch={() => setSearchTerm("")}
        isError={isError}
        onRetry={refetch}
        customEmptyState={
          isPristine ? (
            <PartnerTabEmptyState
              tab="leads"
              onAdd={() => setIsCreateModalOpen(true)}
            />
          ) : undefined
        }
      />
      {userData ? (
        <CreateLeadModal
          isOpen={isCreateModalOpen}
          setIsOpen={setIsCreateModalOpen}
          userData={userData}
          partnerId={partnerId}
          resetPage={resetLeads}
        />
      ) : null}
    </div>
  );
};

interface Props {
  partnerId: string;
}

const PartnerLeadsTab: React.FC<Props> = ({ partnerId }) => {
  const { data: entityFieldsResponse } = useEntityFields(EntityType.LEAD);
  const customFields = useMemo(
    () => entityFieldsResponse?.fields || [],
    [entityFieldsResponse]
  );
  const baseFilters = useMemo(
    () => getPartnerBaseFilters(customFields, partnerId),
    [customFields, partnerId]
  );

  // Wait for the entity fields so the content mounts with the partner scope
  // already resolved and seeds it as the initial filter.
  if (!entityFieldsResponse) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PartnerLeadsTabContent
      customFields={customFields}
      baseFilters={baseFilters}
      partnerId={partnerId}
    />
  );
};

export default PartnerLeadsTab;
